import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError, qrUrl } from "@/shared/api";
import { s } from "@/shared/strings";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Logo } from "@/shared/components/Logo";
import { teamColor, teamName } from "@/shared/components/TeamView";
import { Dices } from "lucide-react";

const FUN_NICKS = [
  "Tucano Veloz",
  "Capivara Ninja",
  "Arara Sônica",
  "Jabuti Turbo",
  "Quati Relâmpago",
  "Onça Astuta",
  "Boto Cor-de-Rosa",
  "Tamanduá Sábio",
];

export function Join() {
  const { code = "" } = useParams();
  const nav = useNavigate();
  const [nick, setNick] = useState("");
  const [team, setTeam] = useState(0); // 0 = atribuição automática balanceada
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const info = useQuery({
    queryKey: ["session", code],
    queryFn: () => api.sessionInfo(code),
    retry: false,
  });

  const randomNick = () => setNick(FUN_NICKS[Math.floor(Math.random() * FUN_NICKS.length)]);
  const rosterMode = info.data?.nicknameMode === "roster";
  const roster = info.data?.roster ?? [];

  const join = async (nickname: string) => {
    if (nickname.length < 2 || busy) return;
    setBusy(true);
    setErr("");
    try {
      const res = await api.joinSession(code, nickname, undefined, team || undefined);
      sessionStorage.setItem(`arena.player.${code}`, res.playerToken);
      sessionStorage.setItem(`arena.nick.${code}`, res.nickname);
      nav(`/play/${code}`);
    } catch (ex) {
      if (ex instanceof ApiError) {
        setErr(
          ex.code === "nickname_offensive"
            ? s.nicknameOffensive
            : ex.code === "nickname_not_in_roster"
              ? s.nicknameNotInRoster
              : ex.code === "nickname_rejected"
                ? s.nicknameTaken
                : ex.code === "room_locked"
                  ? s.roomLocked
                  : ex.code === "room_full"
                    ? s.roomFull
                    : ex.code === "ended"
                      ? s.sessionEnded
                      : ex.message || s.joinError,
        );
      } else {
        setErr(s.joinError);
      }
      setBusy(false);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    void join(nick.trim());
  };

  return (
    <main className="bg-bg flex min-h-dvh flex-col items-center justify-center gap-8 px-6 py-10">
      <Logo size="sm" />
      <h1 className="expanded text-56 text-amber text-center font-black tracking-[0.15em]">
        {code.toUpperCase()}
      </h1>
      {info.data?.title && (
        <p className="text-18 text-chalk-2 -mt-4 font-semibold">{info.data.title}</p>
      )}

      {info.isError && <p className="text-18 text-bad font-semibold">{s.roomNotFound}</p>}
      {info.data?.locked && <p className="text-18 text-warn font-semibold">{s.roomLocked}</p>}
      {info.data?.full && <p className="text-18 text-warn font-semibold">{s.roomFull}</p>}
      {err && <p className="text-18 text-bad font-semibold">{err}</p>}

      {(info.data?.teams ?? 0) > 0 && (
        <div className="flex w-full max-w-md flex-col items-center gap-3">
          <p className="semi-expanded text-18 font-bold">{s.pickTeam}</p>
          <div className="flex flex-wrap justify-center gap-2">
            {Array.from({ length: info.data!.teams! }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setTeam(n)}
                className={`text-18 rounded-full px-4 py-2 font-bold text-white transition-transform ${team === n ? "ring-chalk scale-105 ring-4" : "opacity-70"}`}
                style={{ background: teamColor(n) }}
              >
                {teamName(n)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setTeam(0)}
              className={`text-18 bg-bg-2 border-line rounded-full border px-4 py-2 font-bold ${team === 0 ? "ring-chalk ring-4" : "opacity-70"}`}
            >
              {s.teamAuto}
            </button>
          </div>
        </div>
      )}

      {rosterMode ? (
        <div className="flex w-full max-w-md flex-col items-center gap-5">
          <p className="semi-expanded text-22 font-bold">{s.pickYourName}</p>
          {info.data && roster.length === 0 ? (
            <p className="text-18 text-warn font-semibold">{s.rosterEmpty}</p>
          ) : (
            <div className="grid w-full grid-cols-2 gap-2">
              {roster.map((name) => (
                <Button
                  key={name}
                  type="button"
                  variant="secondary"
                  className="text-18 h-14 font-semibold"
                  disabled={busy}
                  onClick={() => void join(name)}
                >
                  {name}
                </Button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="flex w-full max-w-sm flex-col items-center gap-5">
          <label htmlFor="nick" className="semi-expanded text-22 font-bold">
            {s.nicknamePrompt}
          </label>
          <div className="flex w-full gap-2">
            <Input
              id="nick"
              value={nick}
              onChange={(e) => setNick(e.target.value.slice(0, 20))}
              minLength={2}
              maxLength={20}
              autoComplete="nickname"
              placeholder="Seu apelido"
              className="text-22 h-14"
            />
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="h-14 w-14"
              onClick={randomNick}
              title="Apelido aleatório"
              aria-label="Apelido aleatório"
            >
              <Dices className="h-6 w-6" />
            </Button>
          </div>
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={nick.trim().length < 2 || busy}
          >
            {s.enterRoom}
          </Button>
        </form>
      )}

      <img src={qrUrl(code)} alt="" className="bg-chalk h-20 w-20 rounded-md p-1 opacity-60" />
    </main>
  );
}
